// frontend/src/components/member-dashboard/MemberDashboard.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { profileAPI, bookingsAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import { FaSpinner } from "react-icons/fa";
import SideMenu from "./components/SideMenu";
import CalendarView from "./components/CalendarView";
import SessionModal from "./components/SessionModal";
import BillingBanner from "./components/BillingBanner";
import BookSession from "../BookSession";
import ProfileSection from "./components/ProfileSection";
import ContactReminderBanner from "./components/ContactReminderBanner";
import ActivityFeed from "./components/ActivityFeed";
import MemberCreditsCard from "./components/MemberCreditsCard";
import { useMemberCreditSummary } from "../../hooks/useMemberCreditSummary";
import { useAuth } from "../AuthContext";
import useEsignGateCheck from "../../hooks/useEsignGateCheck";
import {
  getSessionShortDisplayName,
  isMassageSessionType,
} from "../../utils/sessionTypeUtils";
import "./MemberDashboard.css";

/**
 * MemberDashboard component for the member dashboard page
 * @returns {JSX.Element} - Rendered component
 */
const MemberDashboard = () => {
  // State
  const [bookings, setBookings] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState("calendar");
  const [selectedSession, setSelectedSession] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSessionData, setEditingSessionData] = useState({});
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const activeViewRef = useRef(null);
  const hasInitializedViewRef = useRef(false);
  const navigate = useNavigate();
  const { token, initialized } = useAuth();

  // ESIGN-UX-REDESIGN-001 (Story 1) — post-login gate check on dashboard
  // mount. Replaces the booking-flow gate in BookSession.js (kept in place
  // during PR-A as a safety net; removed in PR-B once this gate is smoke-
  // validated in prod). Hook is gated on auth-resolution to avoid firing
  // before AuthContext finishes hydrating (tech doc edge-case table).
  useEsignGateCheck({ enabled: initialized && Boolean(token) });

  const refreshDashboardData = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }

      const [profileResponse, bookingsResponse] = await Promise.all([
        profileAPI.getProfile(),
        bookingsAPI.getMyBookings(),
      ]);

      setProfile(profileResponse.data);
      setBookings(bookingsResponse.data.bookings);
    } catch (err) {
      handleApiError(
        err,
        "MemberDashboard.refreshDashboardData",
        setError,
        "Error loading dashboard data. Please try again."
      );
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    // Check URL parameters for view selection
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get("view");
    if (
      viewParam &&
      ["calendar", "profile", "booking", "activity"].includes(viewParam)
    ) {
      setCurrentView(viewParam);
    }
    refreshDashboardData(true);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", currentView);
    window.history.replaceState({}, "", url.toString());

    if (!hasInitializedViewRef.current) {
      hasInitializedViewRef.current = true;
      return;
    }

    if (activeViewRef.current) {
      activeViewRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [currentView]);

  const scrollActiveViewIntoFocus = useCallback(() => {
    window.setTimeout(() => {
      if (activeViewRef.current) {
        activeViewRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  }, []);

  const handleViewChange = useCallback((view) => {
    if (view === currentView) {
      scrollActiveViewIntoFocus();
      return;
    }

    setCurrentView(view);
    setSelectedSession(null);
    setIsEditing(false);
    setEditingSessionData({});
  }, [currentView, scrollActiveViewIntoFocus]);

  const handleBookingSuccess = useCallback(async () => {
    await refreshDashboardData();
    setCurrentView("calendar");
    setSelectedSession(null);
    setIsEditing(false);
    setEditingSessionData({});

    window.setTimeout(() => {
      if (activeViewRef.current) {
        activeViewRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 75);
  }, []);

  const upcomingSession = useMemo(() => {
    const now = Date.now();

    return [...bookings]
      .filter((booking) => {
        const bookingTime = new Date(booking.appointmentDate).getTime();
        return (
          bookingTime >= now &&
          !["cancelled", "completed", "no-show"].includes(booking.status)
        );
      })
      .sort(
        (left, right) =>
          new Date(left.appointmentDate).getTime() -
          new Date(right.appointmentDate).getTime()
      )[0];
  }, [bookings]);

  // Credits: GET /api/profile/credit-summary (the booking rules), shared
  // with the sidebar. Reloads when the profile changes (after a booking).
  const creditSummaryState = useMemberCreditSummary(profile);

  const bookingPrivileges = useMemo(
    () => profile?.bookingPrivileges || {},
    [profile]
  );
  const corporateProgramStatus = useMemo(
    () => profile?.corporateProgramStatus || null,
    [profile]
  );
  const currentViewLabel =
    {
      calendar: "My Sessions",
      booking: "Book Session",
      activity: "Activity",
      profile: "Profile",
    }[currentView] || "Dashboard";

  const blockerCards = useMemo(() => {
    const blockers = [];

    if (profile && !profile.paymentMethod) {
      blockers.push({
        id: "payment",
        label: "Add payment method",
        description:
          corporateProgramStatus?.enrolled &&
          Number(corporateProgramStatus.nextBookingAmountDue || 0) > 0
            ? corporateProgramStatus.nextBookingMessage
            : "Add a card so booking and add-on charges can be processed smoothly.",
        actionLabel: "Add Payment Method",
        onClick: () => navigate("/payment"),
      });
    }

    if (profile && !profile.contactNumber) {
      blockers.push({
        id: "contact",
        label: "Add contact number",
        description:
          "We need your phone number for important updates about your sessions.",
        actionLabel: "Update Profile",
        onClick: () => handleViewChange("profile"),
      });
    }

    if (
      bookingPrivileges.membershipAccessStatus &&
      bookingPrivileges.membershipAccessStatus !== "active" &&
      bookingPrivileges.membershipAccessMessage
    ) {
      blockers.push({
        id: "membership-status",
        label:
          bookingPrivileges.membershipAccessStatus === "paused"
            ? "Membership paused"
            : "Pause scheduled",
        description: bookingPrivileges.membershipAccessMessage,
        actionLabel: "View Profile",
        onClick: () => handleViewChange("profile"),
      });
    }

    return blockers;
  }, [
    bookingPrivileges,
    corporateProgramStatus,
    handleViewChange,
    navigate,
    profile,
  ]);


  /**
   * Handle session selection
   * @param {string} sessionId - ID of the selected session
   */
  const handleSessionSelect = (sessionId) => {
    const session = bookings.find((b) => b._id === sessionId);
    if (session) {
      setSelectedSession(session);
      setIsEditing(false);
      setEditingSessionData(
        session.sessionType === "infrared"
          ? {
              addGuest: session.addGuest || false,
              halotherapy: session.halotherapy || false,
            }
          : {}
      );
    }
  };

  /**
   * Handle edit field change
   * @param {Event} e - Change event
   */
  const handleEditChange = (e) => {
    const { name, checked } = e.target;
    setEditingSessionData({ ...editingSessionData, [name]: checked });
  };

  /**
   * Handle save changes
   * @param {string} bookingId - ID of the booking to update
   */
  const handleSaveChanges = async (bookingId) => {
    // If not in editing mode, switch to editing mode
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    try {
      const response = await bookingsAPI.updateBooking(
        bookingId,
        editingSessionData
      );

      // Update bookings state
      setBookings(
        bookings.map((booking) =>
          booking._id === bookingId
            ? { ...booking, ...editingSessionData }
            : booking
        )
      );

      // Reset editing state
      setIsEditing(false);
      setSelectedSession(null);
      setEditingSessionData({});

      // Show success message
      const successMessage = document.createElement("div");
      successMessage.className = "success-toast";
      successMessage.textContent =
        response.data.message || "Session updated successfully!";
      document.body.appendChild(successMessage);

      // Remove after 3 seconds
      setTimeout(() => {
        document.body.removeChild(successMessage);
      }, 3000);
    } catch (err) {
      handleApiError(
        err,
        "MemberDashboard.handleSaveChanges",
        (errorMsg) => {
          // Show error message
          const errorToast = document.createElement("div");
          errorToast.className = "error-toast";
          errorToast.textContent = errorMsg;
          document.body.appendChild(errorToast);

          // Remove after 3 seconds
          setTimeout(() => {
            document.body.removeChild(errorToast);
          }, 3000);
        },
        "Failed to update session."
      );
    }
  };

  /**
   * Handle check-in success
   * @param {string} bookingId - ID of the booking that was checked in
   * @param {string} status - New status of the booking
   */
  const handleCheckInSuccess = (bookingId, status) => {
    // Update the booking status in the bookings state
    setBookings(
      bookings.map((booking) =>
        booking._id === bookingId
          ? {
              ...booking,
              status: status,
              checkedInAt: new Date().toISOString(),
            }
          : booking
      )
    );

    // If the checked-in booking is currently selected, update the selected session
    if (selectedSession && selectedSession._id === bookingId) {
      setSelectedSession({
        ...selectedSession,
        status: status,
        checkedInAt: new Date().toISOString(),
      });
    }

    // Show success message
    const successMessage = document.createElement("div");
    successMessage.className = "success-toast";
    successMessage.textContent = "Successfully checked in to session!";
    document.body.appendChild(successMessage);

    // Remove after 3 seconds
    setTimeout(() => {
      document.body.removeChild(successMessage);
    }, 3000);
  };

  /**
   * Handle cancel session
   * @param {string} bookingId - ID of the booking to cancel
   */
  const handleCancelSession = async (bookingId) => {
    const bookingToCancel = bookings.find((booking) => booking._id === bookingId);
    const confirmMessage =
      bookingToCancel && isMassageSessionType(bookingToCancel.sessionType)
        ? "Are you sure you want to cancel this massage? Cancellations 2+ hours before the session receive a full refund, 1-2 hours before receive a 50% refund, and less than 1 hour before receive no refund."
        : "Are you sure you want to cancel this session? Cancellations less than 1 hour before the session will forfeit the session.";

    if (
      !window.confirm(confirmMessage)
    ) {
      return;
    }

    try {
      const response = await bookingsAPI.cancelBooking(bookingId);
      await refreshDashboardData();

      // Reset modal state
      setSelectedSession(null);
      setIsEditing(false);
      setEditingSessionData({});

      // Show success message
      const successMessage = document.createElement("div");
      successMessage.className = "success-toast";
      successMessage.textContent =
        response.data.message || "Session cancelled successfully!";
      document.body.appendChild(successMessage);

      // Remove after 3 seconds
      setTimeout(() => {
        document.body.removeChild(successMessage);
      }, 3000);
    } catch (err) {
      handleApiError(
        err,
        "MemberDashboard.handleCancelSession",
        (errorMsg) => {
          // Show error message
          const errorToast = document.createElement("div");
          errorToast.className = "error-toast";
          errorToast.textContent = errorMsg;
          document.body.appendChild(errorToast);

          // Remove after 3 seconds
          setTimeout(() => {
            document.body.removeChild(errorToast);
          }, 3000);
        },
        "Error cancelling session."
      );
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="loading-container">
        <FaSpinner className="spinner" />
        <p className="loading-msg">Loading your dashboard...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="error-container">
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="retry-button"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="member-dashboard">
      <div className="dashboard-container">
        {/* Sidebar */}
        <aside className="sidebar">
          <SideMenu
            onSelect={handleViewChange}
            currentView={currentView}
            profile={profile}
            selectedSession={selectedSession}
            onAddOnUsed={(type, data) => {
              if (type === "guestPass" || type === "halotherapy") {
                refreshDashboardData();
              }
            }}
          />
        </aside>

        {/* Main Content */}
        <main className="dashboard-content">
          <header className="dashboard-header">
            <h1>
              Welcome back,{" "}
              {profile
                ? profile.preferredName || profile.firstName || "Member"
                : "Member"}
              .
            </h1>
            <p className="dashboard-subtitle">
              Use the buttons below to quickly book, review upcoming sessions,
              or update your account details.
            </p>

            {/* Show billing banner if no payment method */}
            {profile && !profile.paymentMethod && (
              <BillingBanner userId={profile._id} />
            )}
            {/* Show contact reminder banner if no contact number */}
            {profile && !profile.contactNumber && (
              <ContactReminderBanner setCurrentView={handleViewChange} />
            )}
          </header>

          <section className="dashboard-summary">
            <div className="summary-hero">
              <span className="summary-eyebrow">What would you like to do?</span>
              <h2>Everything important is right here.</h2>
              <p>
                Start with the large button below if you want to schedule a new
                session.
              </p>
              <div className="summary-actions">
                <button
                  type="button"
                  className="member-action-button member-action-button-primary"
                  onClick={() => handleViewChange("booking")}
                >
                  Book Session
                </button>
                <button
                  type="button"
                  className="member-action-button member-action-button-secondary"
                  onClick={() => handleViewChange("calendar")}
                >
                  View Sessions
                </button>
                <button
                  type="button"
                  className="member-action-button member-action-button-secondary"
                  onClick={() => handleViewChange("profile")}
                >
                  Update Profile
                </button>
              </div>
            </div>

            {blockerCards.length > 0 && (
              <div className="summary-blockers" aria-live="polite">
                {blockerCards.map((blocker) => (
                  <div key={blocker.id} className="summary-blocker-card">
                    <div>
                      <strong className="summary-blocker-title">
                        {blocker.label}
                      </strong>
                      <p className="summary-blocker-description">
                        {blocker.description}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="summary-blocker-action"
                      onClick={blocker.onClick}
                    >
                      {blocker.actionLabel}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="summary-card-grid">
              <div className="summary-card">
                <span className="summary-card-label">Next Session</span>
                {upcomingSession ? (
                  <strong className="summary-card-value">
                    {new Date(upcomingSession.appointmentDate).toLocaleDateString(
                      [],
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </strong>
                ) : (
                  <span className="summary-card-value summary-card-value-muted">
                    No session booked
                  </span>
                )}
                <p className="summary-card-detail">
                  {upcomingSession
                    ? `${new Date(
                        upcomingSession.appointmentDate
                      ).toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                      })} · ${getSessionShortDisplayName(
                        upcomingSession.sessionType
                      )}`
                    : "Tap Book Session to choose your next visit."}
                </p>
              </div>

              <div className="summary-card">
                <span className="summary-card-label">Your credits</span>
                <MemberCreditsCard
                  summary={creditSummaryState.summary}
                  loading={creditSummaryState.loading}
                  error={creditSummaryState.error}
                  variant="dashboard"
                />
              </div>

              {corporateProgramStatus?.enrolled && (
                <div className="summary-card summary-card-corporate">
                  <span className="summary-card-label">
                    {corporateProgramStatus.organizationCode} Benefit
                  </span>
                  <span className="summary-card-value">
                    {corporateProgramStatus.summaryValue ||
                      (corporateProgramStatus.nextBookingAmountDue > 0
                        ? `$${corporateProgramStatus.corporateRate} corporate rate next`
                        : `${corporateProgramStatus.employeeSponsoredRemaining} of ${corporateProgramStatus.sponsoredSessionsPerEmployeePerMonth} sponsored left`)}
                  </span>
                  <p className="summary-card-detail">
                    {corporateProgramStatus.summaryMessage ||
                      corporateProgramStatus.nextBookingMessage}
                  </p>
                </div>
              )}
            </div>
          </section>

          {isMobile && (
            <section className="mobile-account-summary">
              <details>
                <summary>Account Snapshot</summary>
                <MemberCreditsCard
                  summary={creditSummaryState.summary}
                  loading={creditSummaryState.loading}
                  error={creditSummaryState.error}
                  variant="mobile"
                  showAddOns
                />
              </details>
            </section>
          )}

          {/* Show profile view if selected */}
          {currentView === "profile" ? (
            // Use the ProfileSection component
            <div className="profile-container" ref={activeViewRef}>
              <h2 className="section-title">Your Profile</h2>
              <p className="section-intro">
                Keep your contact and billing details up to date so booking stays
                simple.
              </p>
              <ProfileSection
                profile={profile}
                onProfileUpdate={(updatedProfile) =>
                  setProfile({ ...profile, ...updatedProfile })
                }
              />
            </div>
          ) : (
            <>
              {/* Main Content Area */}
              <section className="dashboard-section" ref={activeViewRef}>
                <div className="current-view-badge">
                  Now viewing: <strong>{currentViewLabel}</strong>
                </div>
                {currentView === "calendar" && (
                  <div className="content-card">
                    <h2 className="section-title">Your Booked Sessions</h2>
                    <p className="section-intro">
                      {isMobile
                        ? "Tap a session below to review details, check in, or make changes."
                        : "Here is a simple list of your upcoming booked sessions. Tap one to review details, check in, or make changes."}
                    </p>
                    <CalendarView
                      bookings={bookings}
                      onSelectSession={handleSessionSelect}
                      displayMode="list"
                    />
                  </div>
                )}
                {currentView === "booking" && (
                  <div className="content-card content-card-highlight">
                    <h2 className="section-title">Book Your Next Sessions</h2>
                    <p className="section-intro">
                      Start below by choosing a day, then select a time and suite.
                    </p>
                    <BookSession
                      bookings={bookings}
                      onSelectSession={handleSessionSelect}
                      onBookingSuccess={handleBookingSuccess}
                      onCancel={() => handleViewChange("calendar")}
                    />
                  </div>
                )}
                {currentView === "activity" && (
                  <div className="content-card activity-container">
                    <h2 className="section-title">Activity History</h2>
                    <p className="section-intro">
                      Review recent bookings and payments in one place.
                    </p>
                    <ActivityFeed />
                  </div>
                )}
              </section>
            </>
          )}
        </main>
      </div>

      {/* Session Modal */}
      {selectedSession && (
        <SessionModal
          session={selectedSession}
          onClose={() => {
            setSelectedSession(null);
            setIsEditing(false);
            setEditingSessionData({});
          }}
          onSaveChanges={handleSaveChanges}
          onCancelSession={handleCancelSession}
          onEditChange={handleEditChange}
          onCheckInSuccess={handleCheckInSuccess}
          editingData={editingSessionData}
          isEditing={isEditing}
        />
      )}
    </div>
  );
};

export default MemberDashboard;
